import { Box, IconButton, Modal, styled, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef } from 'react';
import Network from '../../common/networkHandler/Network';
import Auth from '../../common/auth/auth';
import { LoginType } from '../../_genApi/gameDeploy';
import WindowEventListener from '../../common/WindowEventListener';
import StringUtil from '../../common/util/StringUtil';
import { useLanguage } from '../../contexts/LanguageContext';
import GameMgr from 'src/common/GameMgr';
import IframeUtil from '../../common/util/IframeUtil';

const ModalOuterContainer = styled(Box)(
  () => `
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `
);

const ModalContainer = styled(Box)(
  ({ theme }) => `
    position: fixed;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    background-color: black;
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    outline: none;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `
);

const GameNameDisplay = styled(Typography)(
  () => `
    position: absolute;
    top: 20px;
    left: 20px;
    color: #ffffffd8;
    font-family: Arial, sans-serif;
    font-size: 20px;
    font-weight: 500;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
    z-index: 1200;
  `
);

const CloseButton = styled(IconButton)(
  ({ theme }) => `
    position: absolute;
    top: 12px;
    right: 12px;
    color: ${theme.palette.common.white};
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 1300;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    &:hover {
      background-color: rgba(0, 0, 0, 0.8);
    }

    @media (max-width: 768px) {
      width: 56px;
      height: 56px;
      min-width: 56px;
      min-height: 56px;
      top: 16px;
      right: 16px;
    }
  `
);

const GameIframe = styled('iframe')(
  () => `
    border: none;
    width: 100%;
    height: 100%;
    flex: 1;
  `
);

interface GameModalProps {
  gameId: number;
  open: boolean;
  onClose: () => void;
  gameName?: string;
  /**
   * Language code to load inside the iframe player. Falls back to the user's
   * UI language when omitted. Used by the "play in origin language" flow when
   * a localization pack is missing or the user opted out of translation.
   */
  language?: string;
}

function newMessageId(signature: string, attempt: number) {
  return 'GameModal-' + signature + '-' + StringUtil.randomString() + "-" + Date.now() + "-" + attempt;
}


function buildPlayerUrl(gameId: number, language: string): string {
  const serverUrl = Network.withServerUploadGame().getServerUrl();
  const aiUrl = Network.withServerAssistant().getServerUrl();
  const token = Auth.getLoginToken() || '';

  const base = `${serverUrl}/game/${gameId}/game.html`;
  const params = new URLSearchParams({
    gameId: String(gameId),
    language,
    platformUrl: serverUrl,
    ...(aiUrl ? { aiUrl } : {}),
    ...(token ? { token } : {}),
  });

  return IframeUtil.normalizeDomain(`${base}?${params.toString()}`);
}

const GameModal = ({ gameId, open, onClose, gameName, language: languageOverride }: GameModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { language: userLanguage } = useLanguage();
  const language = languageOverride ?? userLanguage;

  useEffect(() => {
    if (!open || !gameId) return;

    const token = Auth.getLoginToken() || '';
    const messageId = newMessageId('sendGameCredentials', 0);
    let success = false;
    
    const sendMessage = (attempt: number) => {
      if (!iframeRef.current || !iframeRef.current.contentWindow || success) {
        if (attempt >= 10) {
          WindowEventListener.ignoreConfirmation(messageId);
          return;
        }
        
        setTimeout(() => sendMessage(attempt + 1), 500);
        return;
      }

      const messageContent = {
        messageId,
        gameId,
        ssKey: token,
        userName: Auth.getUsername(),
        loginType: Auth.loginType as LoginType,
        avatar: Auth.getCurrentUser()?.avatar,
        source: "user",
      };

      iframeRef.current.contentWindow.postMessage(
        JSON.stringify(messageContent),
        Network.withServerUploadGame().getServerUrl() + '/'
      );

      if (attempt >= 10) {
        WindowEventListener.ignoreConfirmation(messageId);
        return;
      }

      setTimeout(() => {
        if (!success) {
          sendMessage(attempt + 1);
        }
      }, 500);
    };

    WindowEventListener.onConfirmation(messageId, () => {
      console.log('Game credential message sent successfully');
      success = true;
    });

    // Give iframe time to load before sending message
    setTimeout(() => sendMessage(0), 800);

    return () => {
      WindowEventListener.ignoreConfirmation(messageId);
    };
  }, [gameId, open]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {

      // Check if message is from the game iframe
      const gameServerUrl = Network.withServerUploadGame().getServerUrl();
      if (!IframeUtil.isSameOrigin(event.origin, gameServerUrl)) {
        return;
      }

      try {
        const data = event.data;
        console.log('Received message from iframe:', data);

        // Handle different message types
        if (data && typeof data === 'object') {
          switch (data.type) {
            case 'storyEnd':
              console.log('Story ended:', {
                storyId: data.storyId,
                passageId: data.passageId,
                gameId: gameId
              });
              // You can add additional logic here for story end
              GameMgr.markGameEnd(gameId);
              break;
            default:
              console.log('Unknown message type:', data.type, data);
          }
        }
      } catch (error) {
        console.error('Error processing message from iframe:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [gameId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="game-modal"
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
      }}
    >
      <ModalOuterContainer>
        <ModalContainer>
          <CloseButton 
            onClick={() => {
              // Safely access CustomStory from iframe
              const story = IframeUtil.safeAccessIframeContent(iframeRef, 'CustomStory');
              console.log('story', story);

              onClose();
            }} 
            aria-label="close"
            sx={{
              touchAction: 'manipulation',
            }}
            disableRipple={false}
            disableTouchRipple={false}
          >
            <CloseIcon sx={{ fontSize: { xs: '28px', sm: '24px' } }} />
          </CloseButton>
          {open && gameId && (
            <GameIframe
              ref={iframeRef}
              // Pass language and gameId to the game for translation logic
              // Normalize domain to ensure consistency between localhost and 127.0.0.1
              src={buildPlayerUrl(gameId, language)}
              title="Game"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </ModalContainer>
      </ModalOuterContainer>
    </Modal>
  );
};

export default GameModal; 